import { useState } from 'react';
import { themes, type GenderPreset } from '../../theme';
import { Icon } from '../chrome/Icon';
import { Sheet } from '../shared/Sheet';
import { useT } from '../../i18n';

export interface BabyFormData {
  name: string;
  dob: string;          // YYYY-MM-DD
  gender: GenderPreset;
}

interface Props {
  open: boolean;
  mode: 'add' | 'edit';
  /** Pre-fill values (edit mode only). */
  initial?: Partial<BabyFormData>;
  onSave: (data: BabyFormData) => void;
  onClose: () => void;
  saving?: boolean;
}

/**
 * Self-contained sheet for adding or editing a baby profile.
 * Reuses the form fields from the onboarding baby step so the UX is
 * identical — just wrapped in a sheet rather than the onboarding shell.
 */
export function BabyFormSheet({ open, mode, initial, onSave, onClose, saving }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [dob, setDob] = useState(initial?.dob ?? '');
  const [gender, setGender] = useState<GenderPreset>(initial?.gender ?? 'neutral');
  const [touched, setTouched] = useState(false);

  const dobValid = /^\d{4}-\d{2}-\d{2}$/.test(dob) && new Date(dob).toString() !== 'Invalid Date';
  const nameValid = name.trim().length > 0;
  const formValid = nameValid && dobValid;

  const submit = () => {
    setTouched(true);
    if (!formValid || saving) return;
    onSave({ name: name.trim(), dob, gender });
  };

  const t = useT();
  const title = mode === 'add' ? t('profile.addAnotherBaby', 'Add another baby') : t('profile.form.updateTitle', 'Edit baby');

  return (
    <Sheet open={open} onClose={onClose} size="md" maxHeight="92dvh" label={title}>
      <div className="flex flex-col gap-7 px-6 pb-8 pt-0 overflow-y-auto">
        {/* Close-row */}
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {title}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        <h1 className="font-serif text-[28px] leading-tight tracking-tight text-ink">
          {mode === 'add'
            ? t('profile.form.meetTitle', "Let's meet your little one.")
            : t('profile.form.updateTitle', 'Update their details.')}
        </h1>
        <p className="text-[14.5px] leading-relaxed text-ink-2 text-pretty">
          {mode === 'add'
            ? t('profile.form.meetDesc', 'Just three things so milestones line up with their first year. You can change any of this later.')
            : t('profile.form.updateDesc', 'You can change the name, date of birth or theme accent at any time.')}
        </p>

        {/* Name */}
        <div>
          <label htmlFor="bf-name" className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('profile.form.nameLabel', 'Name')}
          </label>
          <input
            id="bf-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('profile.form.namePlaceholder', 'e.g. Lena')}
            className="mt-1.5 w-full border-0 border-b-[1.5px] border-ink bg-transparent pb-2 font-serif text-3xl tracking-tight text-ink outline-none placeholder:text-ink-3"
          />
          {touched && !nameValid && (
            <p className="mt-1.5 text-[12px] text-[#7E3320]">{t('profile.form.nameError', "Please enter your baby's name.")}</p>
          )}
        </div>

        {/* DOB */}
        <div>
          <label htmlFor="bf-dob" className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('profile.form.dobLabel', 'Date of birth')}
          </label>
          <div
            className={[
              'mt-2.5 flex min-h-12 items-center gap-2.5 rounded-xl border bg-surface px-4 transition-colors',
              touched && !dobValid ? 'border-[#B5523B]' : 'border-line focus-within:border-ink',
            ].join(' ')}
          >
            <Icon name="calendar" size={18} color="var(--color-ink-2)" />
            <input
              id="bf-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="flex-1 border-0 bg-transparent py-3 text-[15px] text-ink outline-none"
            />
          </div>
          {touched && !dobValid && (
            <p className="mt-1.5 text-[12px] text-[#7E3320]">{t('profile.form.dobError', 'Please pick a valid date.')}</p>
          )}
        </div>

        {/* Gender accent */}
        <div>
          <span className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('profile.theme', 'Theme accent')}
          </span>
          <p className="mt-1 text-[12px] text-ink-3">
            {t('profile.form.themeDesc', 'Picks the colour scheme. You can change it any time.')}
          </p>
          <div className="mt-3 flex gap-2">
            {(Object.keys(themes) as GenderPreset[]).map((g) => {
              const a = themes[g].accent;
              const isOn = gender === g;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className={[
                    'flex flex-1 min-h-12 items-center justify-center gap-2 rounded-3xl text-[14px] transition-all',
                    isOn ? 'font-semibold text-ink' : 'font-medium text-ink-2',
                  ].join(' ')}
                  style={
                    isOn
                      ? {
                          border: `1.5px solid ${a}`,
                          background: `color-mix(in oklch, ${a} 10%, var(--color-bg))`,
                        }
                      : {
                          border: '1px solid var(--color-line)',
                          background: 'var(--color-surface)',
                        }
                  }
                >
                  <span className="inline-block size-3 rounded-full" style={{ background: a }} />
                  {themes[g].name}
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={submit}
          disabled={saving}
          className="
            mt-4 inline-flex w-full min-h-14 items-center justify-center gap-2.5
            rounded-3xl bg-ink px-6 text-[15.5px] font-semibold text-bg
            disabled:opacity-50
          "
        >
          {saving ? t('common.saving', 'Saving…') : mode === 'add' ? t('profile.form.addBaby', 'Add baby') : t('profile.form.saveChanges', 'Save changes')}
          {!saving && <Icon name="arrow" size={18} color="currentColor" />}
        </button>
      </div>
    </Sheet>
  );
}
